import { useMemo } from "react";
import { InfoScreenColumnsLayout } from "@/pages/info-screen/components/InfoScreenColumnsLayout";
import { TabletIdleScreen } from "@/pages/guest-tablet/components/TabletIdleScreen";
import type { GuestTabletIdleScreen } from "@/types/guestTablet";
import type {
  InfoScreenDesign,
  InfoScreenImageTextOverlay,
  InfoScreenPage,
  InfoScreenTextBlock,
} from "@/types/infoScreen";

function pageDesign(page: InfoScreenPage): InfoScreenDesign {
  return page.design ?? "classic";
}
import "@/pages/guest-tablet/guest-tablet.css";

type Props = {
  page: InfoScreenPage;
  /** Bildindex für Vollflächen-Layouts (gesteuert durch Seitenrotation). */
  slideIndex?: number;
};

function sortedBlocks(blocks: InfoScreenTextBlock[]): InfoScreenTextBlock[] {
  return [...blocks].sort((a, b) => a.sort_order - b.sort_order);
}

function TextBlockArticle({ block }: { block: InfoScreenTextBlock }) {
  return (
    <article className={`guest-tablet-idle-text guest-tablet-idle-text--${block.variant}`}>
      {block.title ? <h2 className="guest-tablet-idle-text-title">{block.title}</h2> : null}
      <p className="guest-tablet-idle-text-body whitespace-pre-line">{block.body}</p>
    </article>
  );
}

function ImageTextOverlay({ overlay }: { overlay: InfoScreenImageTextOverlay }) {
  const style = {
    left: `${overlay.x_percent}%`,
    top: `${overlay.y_percent}%`,
  };
  return (
    <div className="info-screen-image-text-overlay" style={style}>
      <article className={`guest-tablet-idle-text guest-tablet-idle-text--${overlay.variant}`}>
        {overlay.title ? (
          <h2 className="guest-tablet-idle-text-title">{overlay.title}</h2>
        ) : null}
        {overlay.body ? (
          <p className="guest-tablet-idle-text-body whitespace-pre-line">{overlay.body}</p>
        ) : null}
      </article>
    </div>
  );
}

function hasOverlayContent(overlay: InfoScreenImageTextOverlay | null | undefined): boolean {
  if (!overlay) return false;
  return Boolean(overlay.title?.trim() || overlay.body?.trim());
}

export function InfoScreenPageView({ page, slideIndex = 0 }: Props) {
  const images = useMemo(
    () => [...page.images].sort((a, b) => a.sort_order - b.sort_order),
    [page.images],
  );

  const safeSlideIndex =
    images.length > 0 ? ((slideIndex % images.length) + images.length) % images.length : 0;

  const design = pageDesign(page);

  if (page.layout === "columns") {
    return (
      <div className="info-screen-page" data-design={design}>
        <InfoScreenColumnsLayout page={page} slideIndex={safeSlideIndex} />
      </div>
    );
  }

  if (page.layout === "full_bleed" || page.layout === "image_focus") {
    const active = images[safeSlideIndex] ?? images[0];
    const overlay = active?.text_overlay;
    const legacyBlocks = sortedBlocks(page.text_blocks);
    return (
      <div className="info-screen-page" data-design={design}>
      <div
        className={`guest-tablet-idle-screen info-screen-page--${page.layout}`}
        aria-live="polite"
      >
        {active ? (
          <figure className="info-screen-full-bleed-image">
            <img src={active.image_url} alt={active.alt_text || ""} />
            {active.caption ? (
              <figcaption className="guest-tablet-idle-slide-caption">{active.caption}</figcaption>
            ) : null}
            {hasOverlayContent(overlay) ? <ImageTextOverlay overlay={overlay!} /> : null}
          </figure>
        ) : null}
        {!hasOverlayContent(overlay) && legacyBlocks.length > 0 ? (
          <div className="info-screen-full-bleed-overlay">
            {legacyBlocks.map((block) => (
              <TextBlockArticle key={block.id} block={block} />
            ))}
          </div>
        ) : null}
      </div>
      </div>
    );
  }

  const idleScreen: GuestTabletIdleScreen = {
    enabled: true,
    slideshow_interval_seconds: page.slideshow_interval_seconds,
    show_workstation_hint: false,
    images: page.images,
    text_blocks: page.text_blocks.map((b) => ({
      id: b.id,
      title: b.title,
      body: b.body,
      variant: b.variant,
      sort_order: b.sort_order,
    })),
  };

  const centered = page.layout === "centered" || (page.layout === "split" && images.length === 0);

  return (
    <div className="info-screen-page" data-design={design}>
      <div
        className={
          centered
            ? "guest-tablet-idle-screen guest-tablet-idle-screen--centered"
            : undefined
        }
      >
        <TabletIdleScreen idleScreen={idleScreen} />
      </div>
    </div>
  );
}
